import PropTypes from 'prop-types'
import { NavLink } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Badge from './Badge'
import Clickable from './Clickable'
import WithAddon from './WithAddon'
import makeHasPermission from '../modules/authentication/selectors/makeHasPermission'
import cn from '../modules/cn'

function NavigationItem({
  Icon,
  label,
  badge,
  to,
  href,
  onClick,
  permissions,
  permissionsWithRegex,
  navigationItemProps,
  addonProps,
  className,
}) {
  const baseStyle = 'text-sm font-bold flex flex-row items-center text-gray-700 hover:text-theme-700'
  const activeStyle = 'text-theme-500 border-theme-500'
  const badgeStyle = 'mb-0.5 bg-theme-700'

  const hasPermission = makeHasPermission()
  const isPermitted = useSelector((state) => hasPermission(state, {
    permissions,
    permissionsWithRegex,
  }))

  const children = (
    <WithAddon
      wrapperClassName="items-center space-x-1"
      leadingAddonClassName="text-[1.5em]"
      leadingAddon={Icon && <Icon />}
      trailingAddon={badge && <Badge className={badgeStyle}>{badge}</Badge>}
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...addonProps}
    >
      {label}
    </WithAddon>
  )

  if (to) {
    return isPermitted && (
      <NavLink
        to={to}
        title={label}
        className={({ isActive }) => cn(
          'group',
          baseStyle,
          isActive && activeStyle,
          className,
        )}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...navigationItemProps}
      >
        {children}
      </NavLink>
    )
  }

  if (href || onClick) {
    return isPermitted && (
      <Clickable
        href={href}
        onClick={onClick}
        title={label}
        className={cn(
          'group',
          baseStyle,
          className,
        )}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...navigationItemProps}
      >
        {children}
      </Clickable>
    )
  }

  return (isPermitted && (
    <div
      title={label}
      className={cn(
        'group',
        'text-sm font-bold flex flex-row items-center text-black',
        className,
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...navigationItemProps}
    >
      {children}
    </div>
  ))
}

NavigationItem.propTypes = {
  Icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.object]).isRequired,
  label: PropTypes.string,
  badge: PropTypes.string,
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
    hash: PropTypes.string,
  })]),
  href: PropTypes.string,
  onClick: PropTypes.string,
  permissions: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string.isRequired),
    PropTypes.string,
  ]),
  permissionsWithRegex: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string.isRequired),
    PropTypes.string,
    PropTypes.object,
  ]),
  navigationItemProps: PropTypes.shape({}),
  addonProps: PropTypes.shape(WithAddon.propTypes),
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
}

NavigationItem.defaultProps = {
  label: null,
  to: null,
  href: null,
  onClick: null,
  badge: null,
  permissions: null,
  permissionsWithRegex: null,
  navigationItemProps: null,
  addonProps: null,
  className: null,
}

export default NavigationItem
